'use client'
import { Chat as ChatType } from '@/app/lib/types'
import { convertToUIMessages } from '@/lib/utils'
import { generateId } from 'ai'
import { differenceInMinutes, formatDistanceStrict } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineX
} from 'react-icons/hi'
import ChatIcon from '../icons/chat-icon'
import ChatIcon34 from '../icons/chat-icon-34'
import { Chat } from './chat'
import { useSearchParams } from 'next/navigation'
import { useTheme } from '../lib/themeContext' // Import the theme context

const AnimationSettings = {
  transition: { duration: 0.25 },
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
}

export function ChatContainer() {
  const { primary, accent, secondary, logo, title } = useTheme() // Consume the theme context
  const [selected, setSelected] = useState<string | null>(null)
  const [chat, setChat] = useState<ChatType | null>(null)
  const [readOnly, setReadOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const searchParams = useSearchParams()

  useEffect(() => {
    const storedChatId =
      searchParams.get('chatId') || localStorage.getItem('chatId')
    if (storedChatId) {
      fetch(`/api/chat/messages?chatId=${storedChatId}`)
        .then(res => res.json())
        .then(data => {
          setChat(data.chat)
          if (loading) {
            const lastMessageTime = new Date(
              localStorage.getItem('lastChatDate')!
            )
            if (
              differenceInMinutes(new Date(), lastMessageTime) < 5 ||
              searchParams.get('chatId')
            ) {
              setSelected(storedChatId)
            }
          }
          if (searchParams.get('chatId')) {
            setReadOnly(true)
          }
          setLoading(false)
        })
        .catch(err => {
          console.error('Failed to fetch messages:', err)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [selected])

  const messages = chat?.messages
    ? convertToUIMessages(chat.messages)
    : undefined

  const handleClose = () => {
    window.parent.postMessage({ type: 'closeIframe' }, '*')
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <AnimatePresence initial={false}>
        {selected ? (
          <motion.div
            className="absolute flex h-screen w-full flex-col items-center justify-center"
            key={'chat'}
            {...AnimationSettings}
          >
            <div className="flex size-full flex-1 flex-col overflow-hidden">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#D9D9D9] bg-white px-4 py-2.5 shadow-md">
                <div className="flex items-center gap-3.5">
                  {!readOnly && (
                    <button
                      onClick={() => {
                        return setSelected(null)
                      }}
                    >
                      <HiOutlineChevronLeft className="size-5" />
                    </button>
                  )}
                  <div className="flex items-center gap-0.5">
                    <div>
                      <ChatIcon fill={accent} />
                    </div>
                    <div className="font-bold" style={{ color: secondary }}>
                      {title}
                    </div>
                  </div>
                </div>
                {!readOnly && (
                  <div>
                    <button onClick={handleClose}>
                      <HiOutlineX />
                    </button>
                  </div>
                )}
              </div>
              <Chat
                readOnly={readOnly}
                id={selected}
                savedMessages={messages}
                initialCurrentPrompts={
                  chat && chat.chatId === selected
                    ? JSON.parse(localStorage.getItem('currentPrompts')!)
                    : undefined
                }
                initialShowFeedback={
                  chat && chat.chatId === selected
                    ? JSON.parse(localStorage.getItem('showFeedback')!)
                    : undefined
                }
                initialWasPromptClicked={
                  chat && chat.chatId === selected
                    ? JSON.parse(localStorage.getItem('promptClicked')!)
                    : undefined
                }
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="absolute flex h-screen w-full flex-col items-center justify-center"
            key={'home'}
            {...AnimationSettings}
          >
            <div className="flex w-full flex-1 flex-col overflow-hidden">
              <div
                className="flex justify-between px-4 py-3 shadow-md"
                style={{ backgroundColor: primary }}
              >
                {logo}
                <button onClick={handleClose}>
                  <HiOutlineX className="text-white" />
                </button>
              </div>
              <div className="flex flex-1 flex-col gap-11 bg-[linear-gradient(180deg,_#FFF_0%,_#E8E8E8_82.07%)] px-4 pt-10">
                <div className="flex flex-col gap-0.5">
                  <div
                    className="text-3xl font-bold"
                    style={{ color: `${accent}C9` }}
                  >
                    Hi There 👋
                  </div>
                  <div className="text-3xl font-bold" style={{ color: accent }}>
                    How can we help?
                  </div>
                </div>
                <div className="flex flex-col gap-5">
                  <button
                    onClick={() => {
                      const id = generateId()
                      return setSelected(id)
                    }}
                    className="rounded-lg border px-2.5 py-4 text-left shadow-md"
                  >
                    <div className="flex gap-3">
                      <div>
                        <ChatIcon34 fill={accent} />
                      </div>
                      <div className="flex flex-1 flex-col gap-2 overflow-hidden">
                        <div className="font-bold" style={{ color: secondary }}>
                          Start New Chat
                        </div>
                        <div className="text-xs">
                          Let us help you find what you’re looking for.
                        </div>
                      </div>
                      <div className="self-center">
                        <HiOutlineChevronRight
                          className="h-5 w-5"
                          style={{ color: primary }}
                        />
                      </div>
                    </div>
                  </button>
                  {chat && (
                    <button
                      onClick={() => {
                        return setSelected(chat.chatId)
                      }}
                      className="rounded-lg border px-2.5 py-4 text-left shadow-md"
                    >
                      <div className="flex gap-3">
                        <div>
                          <ChatIcon34 fill={accent} />
                        </div>
                        <div className="flex flex-1 flex-col gap-2 overflow-hidden">
                          <div
                            className="font-bold"
                            style={{ color: secondary }}
                          >
                            Resume Chat
                          </div>
                          <div className="line-clamp-1 text-xs">
                            {localStorage.getItem('lastChatMessage')}
                          </div>
                          <div className="text-xs" style={{ color: secondary }}>
                            {formatDistanceStrict(
                              new Date(),
                              new Date(localStorage.getItem('lastChatDate')!)
                            )}{' '}
                            ago
                          </div>
                        </div>
                        <div className="self-center">
                          <HiOutlineChevronRight
                            className="h-5 w-5"
                            style={{ color: primary }}
                          />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
